import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { format } from 'date-fns'
import Markdown from 'marked-react'

import { markdownLinkRenderer } from '@/utils/helpers'
import { Card } from '@/components/cards/Card'
import { Button } from '@/components/buttons/Button'
import { Loader } from '@/components/loaders/Loader'
import { ExternalLink } from '@/components/ExternalLink'
import { MediaPlayer } from '@/components/media/MediaPlayer'
import { MediaPlayerPlaceholder } from '@/components/media/MediaPlayerPlaceholder'
import { fetchAudioForCallId } from '@/reducers/media/media.actions'
import { updateMediaPlayer } from '@/reducers/media/media.redux'

export const EvidenceCard = ({ evidence, isMessageEvidence = false }) => {
  const dispatch = useDispatch()
  const activeCallId = useSelector((state) => state.media.mediaPlayer.callId)
  const isActive = activeCallId === evidence.callId
  const [callAudio, setCallAudio] = useState(null)
  const [isLoadingAudio, setIsLoadingAudio] = useState(false)

  // Load this call's recording on demand (on click), rather than eagerly for
  // every evidence card when the insight drawer opens — which fired a
  // GET /api/calls/{id}/audio per card up front. Deferring keeps a drawer with
  // many evidence cards from bursting the audio endpoint's rate limit.
  const loadAudio = async () => {
    if (isLoadingAudio || callAudio) {
      return
    }
    setIsLoadingAudio(true)
    try {
      const response = await fetchAudioForCallId(evidence.callId, dispatch)
      setCallAudio(
        response
          ? {
              call_id: evidence.callId,
              audio_url: response.audioUrl,
              audio_status: response.audioStatus,
              audio_url_expiration: response.audioUrlExpiration,
            }
          : { call_id: evidence.callId, audio_status: 'unavailable' }
      )
    } finally {
      setIsLoadingAudio(false)
    }
  }

  const handleSelectMediaPlayer = (progress = evidence.anchorTime || 0) => {
    if (!callAudio || callAudio.audio_status !== 'available') {
      return
    }

    dispatch(
      updateMediaPlayer({
        callId: callAudio.call_id,
        audioUrl: callAudio.audio_url,
        audioUrlExpiration: callAudio.audio_url_expiration,
        audioStatus: callAudio.audio_status,
        videoUrls: [],
        videoStatus: 'not_available',
        isPlaying: true,
        audioError: false,
        videoError: false,
        progress,
      })
    )
  }

  const renderMarkdown = (content) => (
    <Markdown gfm breaks renderer={markdownLinkRenderer}>
      {content}
    </Markdown>
  )

  // /messages/:id resolves the id as a Message.id (the route action calls
  // email-ingestion/thread/id/{id}), so for messaging evidence we link with
  // the resolved messageId from the backend; falling back to callId only if
  // the resolution failed (old data) leaves the link broken in that edge
  // case but matches today's behavior.
  const messagingTargetId = evidence.messageId ?? evidence.callId
  const evidenceLink = isMessageEvidence
    ? `/messages/${messagingTargetId}`
    : `/call-explorer/${evidence.callId}`

  return (
    <Card>
      <div className="flex flex-align-center flex-space-between insight-card__meta">
        <div className="flex flex-align-center small-gap">
          {!isMessageEvidence &&
            (isActive ? (
              <MediaPlayer useMinimalUi hideShareButton />
            ) : isLoadingAudio ? (
              <Loader label="Loading recording..." />
            ) : !callAudio ? (
              <Button small onClick={loadAudio} data-testid={`load-recording-${evidence.callId}`}>
                Load recording
              </Button>
            ) : callAudio.audio_status !== 'available' ? (
              <span className="audio-unavailable-text">Recording not available</span>
            ) : (
              <MediaPlayerPlaceholder
                callAudio={callAudio}
                handleSelectMediaPlayer={handleSelectMediaPlayer}
                useMinimalUi
              />
            ))}
          <ExternalLink
            to={evidenceLink}
            label={
              isMessageEvidence ? `Conversation ${messagingTargetId}` : String(evidence.callId)
            }
            target="_blank"
          />
        </div>
        <span>{format(new Date(evidence.createdAt), 'MMM d, yyyy')}</span>
      </div>
      {!isEmpty(evidence.excerpt) && (
        <div className="insight-evidence__quote">{renderMarkdown(evidence.excerpt)}</div>
      )}
      {!isEmpty(evidence.explanationSummary || evidence.explanation) && (
        <div className="insight-evidence__explanation">
          {renderMarkdown(evidence.explanationSummary || evidence.explanation)}
        </div>
      )}
    </Card>
  )
}
